import React, { KeyboardEvent, useRef, useState } from 'react';
import {
  AttachmentDisplay,
  AttachmentInfo,
} from 'src/components/Messages/AttachmentsPanel';
import { validateAttachments } from 'src/components/ReachProSupport/attachmentValidation';
import { Content, useContent } from 'src/contexts/ContentContext';
import { Button, Stack, TextArea } from 'src/core/ui';
import { AddFileIcon } from 'src/svgs/AddFileIcon';
import { ContentId } from 'src/utils/constants/contentId';

import * as styles from './ReachProSupportCaseChatInput.css';

interface ReachProSupportCaseChatInputProps {
  onSend: (message: string, attachments: File[]) => void;
  isLoading?: boolean;
}

function toAttachmentInfo(f: File): AttachmentInfo {
  return {
    attachmentId: f.name,
    name: f.name,
    size: f.size,
  };
}

export function ReachProSupportCaseChatInput({
  onSend,
  isLoading,
}: ReachProSupportCaseChatInputProps) {
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | undefined>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const addFiles = (incoming: File[]) => {
    const result = validateAttachments(files, incoming);
    if (result.error) {
      setAttachmentError(result.error.message);
      return;
    }
    setAttachmentError(undefined);
    setFiles((prev) => [...prev, ...result.accepted]);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    if (selected.length > 0) {
      addFiles(selected);
    }
    // Reset so the same file can be re-selected after removal
    e.target.value = '';
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const dropped = Array.from(e.dataTransfer?.files ?? []);
    if (dropped.length > 0) {
      addFiles(dropped);
    }
  };

  const handleDeleteAttachment = (attachment: AttachmentInfo) => {
    setFiles((prev) => prev.filter((f) => f.name !== attachment.attachmentId));
    setAttachmentError(undefined);
  };

  const handleSend = () => {
    if (message.trim() || files.length > 0) {
      onSend(message.trim(), files);
      setMessage('');
      setFiles([]);
      setAttachmentError(undefined);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const typeYourMessageHere = useContent(ContentId.TypeYourMessageHere);

  return (
    <div
      className={styles.inputContainer}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.gif,.pdf"
        multiple
        className={styles.hiddenFileInput}
        onChange={handleFileInputChange}
      />
      <Stack direction="column" alignItems="end" gap="l" width="full">
        {files.length > 0 && (
          <Stack direction="row" gap="s" width="full" flexWrap="wrap">
            {files.map((f) => (
              <AttachmentDisplay
                key={f.name}
                attachmentInfo={toAttachmentInfo(f)}
                showDelete={true}
                onDeleteClicked={handleDeleteAttachment}
                isDownloadable={false}
              />
            ))}
          </Stack>
        )}
        {attachmentError && (
          <div className={styles.attachmentError}>{attachmentError}</div>
        )}
        <div className={styles.inputRow}>
          <button
            type="button"
            className={styles.paperclipButton}
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading}
            title="Attach files"
            aria-label="Attach files"
          >
            <AddFileIcon size="20px" />
          </button>
          <div className={styles.textAreaWrapper}>
            <TextArea.Root>
              <TextArea.Area
                maxLength={2000}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={typeYourMessageHere}
                disabled={isLoading}
              />
            </TextArea.Root>
          </div>
          <Button
            onClick={handleSend}
            disabled={(!message.trim() && files.length === 0) || isLoading}
          >
            <Content id={ContentId.Reply} />
          </Button>
        </div>
      </Stack>
    </div>
  );
}
